import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CircularProgress, Box } from "@mui/material";

import {
  Button as MuiButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { baseUrl } from "../../config";
import { Delete, Download, Search } from "@mui/icons-material";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useAuth from "../../hooks/useAuth";
import { uploadFile, fetchFiles } from "../../redux/slices/file";
import { format } from "date-fns";
import PDFViewerModal from "../PDFViewer";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

function FileList({
  onComplete,
  rows,
  handleShowFiles,
  status,
}: {
  onComplete: (success: boolean) => void;
  rows: any[];
  handleShowFiles: (file: any, isOpen: boolean) => void;
  status: string;
}) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        await dispatch(
          uploadFile({ file, associationId: associationId })
        ).unwrap();

        // Refetch files after successful upload
        await dispatch(fetchFiles(associationId));

        onComplete(true);
      } catch (error) {
        onComplete(false);
      } finally {
        setUploading(false);
        // Reset file input so the same file can be uploaded again if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleDownload = async (filename: any) => {
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element, set the URL and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Cleanup: remove the link and revoke the object URL
      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      onComplete(true);
    } catch (error) {
      console.error("Error downloading the file:", error);
      onComplete(false);
    }
  };

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tiedostot
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={"small"}
          onClick={handleButtonClick}
          disabled={uploading}
        >
          Lisää
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </CardContent>
      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell>Nimi</TableCell>
              <TableCell>Tyyppi</TableCell>
              <TableCell>Koko (mb)</TableCell>
              <TableCell>Luotu</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" || uploading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Ei tiedostoja
                </TableCell>
              </TableRow>
            ) : (
              (rows || []).map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.originalname}</TableCell>
                  <TableCell>{row.mimetype}</TableCell>
                  <TableCell>{(row.size / (1024 * 1024)).toFixed(2)}</TableCell>
                  <TableCell>
                    {format(new Date(row.createdAt), "dd-MM-yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Download
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDownload(row.filename)}
                    />{" "}
                    <Search
                      style={{ cursor: "pointer" }}
                      onClick={() => handleShowFiles(row, true)}
                    />{" "}
                    {currentUser?.isSuperAdmin ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        //onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Files() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [didFetch, setDidFetch] = useState(false);
  const { files = [], status = "idle" } = useAppSelector(
    (state) => state.file || {}
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // const handleOpen = () => {
  //   dispatch(openUserForm());
  // };

  // const handleShowUser = (user: any) => {
  //   dispatch(setUser(user));
  //   dispatch(openUserForm());
  // };

  const handleShowFiles = (file: any, isOpen: boolean) => {
    handleShowInFileModal(file, isOpen);
  };

  const handleShowInFileModal = (file: any, isOpen: boolean) => {
    const filename = file.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  // const handleEditUser = (user: User | undefined) => {
  //   dispatch(setUser(user));
  //   dispatch(openUserForm());
  // };

  // const handleDelete = (user: User) => {
  //   // TODO confirm
  //   dispatch(deleteUser(user.id));
  // };

  useEffect(() => {
    if (!didFetch && status === "idle" && associationId) {
      dispatch(fetchFiles(associationId));
      setDidFetch(true);
    }
  }, [dispatch, currentUser, status, didFetch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Tiedostot" />
      <Typography variant="h3" gutterBottom display="inline">
        Tiedostot
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tiedostot</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      <FileList
        rows={files}
        status={status}
        handleShowFiles={handleShowFiles}
        onComplete={(success: boolean) => {
          setProgress(100);
          if (success) {
            setSnackbarMessage("Tiedosto käsitelty onnistuneesti!");
            setSnackbarSeverity("success");
          } else {
            setSnackbarMessage("Tiedoston käsittely epäonnistui.");
            setSnackbarSeverity("error");
          }
          setOpenSnackbar(true);
        }}
        // handleShowUser={handleShowUser}
        // handleEditUser={handleEditUser}
        // handleDelete={handleDelete}
        // openForm={open}
      />

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Files;
